import {cloneElement, Fragment, isValidElement, useEffect, useLayoutEffect, useRef, useState} from "react";
import {CallbackRegistry, debounce, Debounced, DebounceOptions, funcOrValOr, getCallStack, isObject, makeArr, objMap, objRmKeys} from "./CommonJS";
import {tooltipIfNeeded} from "./CommonHtml";

export const getElmOfRef = r =>
    typeof r === "string"? document.getElementsByClassName(r)?.[0] :
        (r?.current ?? r) instanceof Element? (r?.current ?? r) : null;

// sets a ref object, in case you need to override the ref= callback.
export const initRef = (ref, el) => typeof ref === "function"? ref(el) : ref && (ref.current = el);

// HACK: tell if current code is while component is rendering.
export const isInRender = () => getCallStack().includes("renderWithHooks");

/**
 * Improved version of useState:
 *  Returns a persistent state object.
 *  Allows direct modification of state (without using setState), even during render.
 *  setState can be used when requiring rerender. only modifies the passed keys, and only rerenders if any key actually modified.
 *  Closures always get the up-to-date state version.
 *  Also returns generic renderer() and a 2nd vals object that can be utilized.
 *
 * @param init
 * @param firstInit
 @return {{vals: {}, resetState, setState, onChange, state, rerender: (function(): void)}}
 * setState with empty object clears all state object.
 * setState with a cb function will call the cb with the current state to get a new state change.
 * setState returns true if values changed and rerender applied.
 * resetState will reapply init object values. If no init object or empty, state will be cleared. If init is a function, it should return an init object.
 */
export function useStateEx<S extends object>(init: Partial<S> = {}, firstInit?: Partial<S>) {
    const rerender = useRerender();
    const vals = useRef<{
        state:              S;
        setStateInRender:   (u: Partial<S> | ((s: S) => Partial<S>)) => boolean;
        setState:           (u: Partial<S> | ((s: S) => Partial<S>)) => boolean;
        resetState:         () => void;
        onChange:           <K extends keyof S>(field: K, value: S[K]) => boolean;
        clear:              () => void;
    }>({} as any).current;

    if (componentInit(vals)) Object.assign(vals, {
        state: Object.assign({}, funcOrValOr(init), firstInit),
        setStateInRender: obj => {
            obj = funcOrValOr(obj, state);         // allow setState to receive a function which called with the current state and returns the new state.
            if (!Object.keys(obj).length && Object.keys(state).length)
                clear();
            else {
                if (Object.entries(obj).every(([k, v]) => state[k] === v)) return false;
                Object.assign(state, obj);
            }
            return true;
        },
        setState: obj => setStateInRender(obj) && (rerender(), true),
        resetState: () => (setStateInRender({}), setState(funcOrValOr(init))),
        onChange: (f, v) => setState({[f]: v} as any),      // simplify migration from useInputChange
        clear: () => objRmKeys(state, Object.keys(state)),
    });

    const {state, setState, setStateInRender, onChange, resetState, clear} = vals;
    return {state, setState, setStateInRender, onChange, resetState, clear, rerender, vals};
}

/**
 * @param className override className string passed by caller
 * @param optClasses strings | booleans variable number of parameters to check vs className. if basename before '-' exists in className, it will be skipped.
 * @return {string}  joined className string of non-overlapping classes + caller className
 */
export const tailCheck = (className, ...optClasses) =>
    optClasses.filter(c => c && typeof c === "string" &&
        !(new RegExp(`(?<!:)\\b${c.replace(/[()[\]]/g, "").before("-").before(" ")}\\b`).test(className)))
        .concat(className || "")
        .join(" ");

export const flexRow = (className, ...children) =>
    <div className={`flex items-center ${className}`}>
        {children.filter(a => a || a === "" || a === 0)
            .map((a, i) => <Fragment key={i}>{a}</Fragment>)}
    </div>;

// generates html text with span className in parts within.
// textClassing`A prefix ${"text-subtitle1"}vdi-ny${0}, will like ${"text-subtitle1"}vdi-ny1234${0}.`
//
export const textClassing = (strs, ...vals) =>
    strs.map((tx, i) => !i || !vals[i-1]? tx : <span key={i} className={vals[i-1]}>{tx}</span>);


export function useRerender() {
    const [, forceRerender] = useState(0);
    return (skipIfInRender?: boolean | number) => (!skipIfInRender || !isInRender()) && forceRerender(tick => tick + 1);
}

// Returns true if component runs first time or if any deps(endencies) change.
//
export function componentInit(vals, deps: unknown[] = [], kInit?) {
    kInit = kInit ?? "_compInit";
    if (vals[kInit]?.equals(deps))
        return false;
    vals[kInit] = deps;
    return true;
}

// Generates a jsx of component hierarchy with condition inclusion at any level.
// For example, if you would like to generate a child component, but its parent should only conditionally render.
//
export const jsxTree = (...jsxObjs) =>
    jsxObjs.filter(Boolean).reverse()
        .reduce((a, b) => (!a ? b : cloneElement(b, {children: a})), null);

export const objToArrKH = obj => isObject(obj) && Object.entries(obj).map(([h, k]) => ({
    h: h.fromKebabCase().splitCamel().capitalizeFirst(),
    k: Array.isArray(k) ? k.map(v => isValidElement(v) ? v: isObject(v) ? objToArrKH(v) : v) : isValidElement(k) ? k: isObject(k) ? objToArrKH(k) : k
}));


export interface UseDataSingletonParams<T> {
    key: string;
    fetcher: (cb: (val: T) => void) => void;
    resetOnLastUnuse?: boolean;
    onCtor?: (refetch: () => void) => void;
    onDtor?: (refetch: () => void) => void;
    init?: T;
}

const dataSingletons = {};
export const dataSingletonsClear = () => objRmKeys(dataSingletons, Object.keys(dataSingletons));

/**
 * A React mechanism to reuse initialized data once across any future usage in session.
 * @param key - Unique key identifying the singleton
 * @param fetcher - initializer func, getting callback as parameter, to call when got value.
 * @param resetOnLastUnuse - if true, when last user of this singleton key destructs, the value of the singleton will revert to undefined.
 * @param onCtor - function(fetcher)? issued after first subscriber registers.
 * @param onDtor - function(fetcher)? issued after last subscriber unregisters.
 * @param init - if provided and singleton has no value yet, seeds curVal so getCur() returns it immediately.
 * @return {{val: unknown, refetch: CallbackRegistry.refetch, setVal: ((function(*): *)|*)}}
 */

export function useDataSingleton<T>({key, fetcher, resetOnLastUnuse, onCtor, onDtor, init}: UseDataSingletonParams<T>) {
    const regObj = dataSingletons[key] || (dataSingletons[key] = new CallbackRegistry(undefined, init));
    const [val, setVal] = useState(regObj.getCur());

    useEffect(() => {
        if (!regObj.fetcher)        // fetcher set here, as fetches should be performed in useEffect.
            regObj.setFetcher(fetcher);

        const unsubscribe = regObj.subscribe(setVal);           // subscribe returns unsubscribe function
        if (regObj.subs.length === 1)
            onCtor?.(regObj.refetch);
        return () => {
            unsubscribe();
            if (resetOnLastUnuse && !regObj.subs.length) {
                delete dataSingletons[key];
                onDtor?.(regObj.refetch);
            }
        };
    }, []);

    return {val, setVal: regObj.setVal, refetch: regObj.refetch};
}


interface UseEventListenerParams {
    eOn?: EventTarget;                      // Target to bind the event to, defaults to window
    sEv: string;                            // Event name (e.g., 'click')
    cb: EventListenerOrEventListenerObject; // Callback - can be an ad hoc function - original cb is retained in the remove closure parameters.
    captureOrOpts?: boolean | AddEventListenerOptions; // true | { capture, once, passive }
    // captureOrOpts: if boolean - means capture. otherwise object:
    //      capture: boolean :  dispatch listener before any EventTarget beneath it in the DOM tree. default false.
    //      once: boolean :     listener removed automatically after first invocation. default false.
    isEnabled?: boolean;                    // Should the listener be active
}
export function useEventListener({eOn = window, sEv, cb, captureOrOpts, isEnabled = true}: UseEventListenerParams) {
    const vals = useRef<{remove?: () => void}>({}).current;

    useEffect(() => {
        if (!isEnabled) vals.remove?.();
        else if (!vals.remove && eOn) {
            eOn.addEventListener(sEv, cb, captureOrOpts);
            vals.remove = () => {
                eOn.removeEventListener(sEv, cb, captureOrOpts);
                vals.remove = undefined;
            };
        }
        return vals.remove;
    }, [!!isEnabled]);
}

// Ensures debouncer cancelling when component destroyed.
//
export function useDebounce<T extends(...args: any[]) => any>(
        cb: T,
        delay?: number | (() => number),
        opts: DebounceOptions = {}
): Debounced<T> {
    const vals = useRef<{debounced?: Debounced<T>}>({}).current;
    if (componentInit(vals))
        vals.debounced = debounce(cb, delay, opts);
    useEffect(() => () => vals.debounced.cancel(), []);
    return vals.debounced;
}

// example: useObserver(ResizeObserver, rTbl, ["height"], calcSizes, {noInit: 1, debounceMs: 100})
// opts:
//  noInit: unless positive, after initial html layout, cb will be called.
//  debounceMs: number of millis to debounce cb call, unless not called in last debounceMs millis.
//
export function useObserver(observer, el, metrics, cb, opts: {debounceMs?: number, noInit?: boolean | number} = {}) {
    const elm = getElmOfRef(el);
    if (!Array.isArray(metrics)) metrics = [metrics];

    const debounced = useDebounce(cb, opts.debounceMs ?? 0, {leading: true});

    useLayoutEffect(() => {
        if (!elm) return;
        const prevVals = {};
        const hObserver = new observer(([{contentRect}]) => {
            const isInit = !prevVals.hasOwnProperty(metrics[0]);
            if (metrics.every(m => contentRect[m] === prevVals[m])) return;
            Object.assign(prevVals, objMap(metrics, m => contentRect[m]));
            if (!opts.noInit || !isInit) debounced(contentRect);
        });
        hObserver.observe(elm);

        return () => hObserver?.disconnect();            // stop listening to changes
    }, [elm]);
}


const c_defaultTheme = "dark";

export function useColorModeTheme() {
    const {val: curTheme, setVal} = useDataSingleton({
        key: "_app_theme",
        fetcher: onFetch => onFetch(applyTheme(localStorage.theme ?? c_defaultTheme)),
    });

    function applyTheme(theme) {
        theme = theme || (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "");
        if (localStorage.theme)
            document.documentElement.classList.remove(localStorage.theme);
        if (theme)
            document.documentElement.classList.add(theme);
        document.documentElement.setAttribute("data-theme", theme || c_defaultTheme);
        return localStorage.theme = theme;
    }

    // current themes: "light" : "dark" : "" (system default))
    return {curTheme, setTheme: theme => setVal(applyTheme(theme))};
}


// Returns a function that tells if the component is still mounted.
//
export function useIsMounted() {
    const vals = useRef<{isMounted?: () => boolean; isDestroyed?: boolean}>({}).current;
    if (!vals.isMounted)
        vals.isMounted = () => !vals.isDestroyed;

    useEffect(() => () => void (vals.isDestroyed = true), [vals]);

    return vals.isMounted;
}

// Encapsulate a useFoo react hook into a component.
// Only useful if you want to encapsulate a useFunc as a subcomponent rather than rerender the parent.
// E.g. use at the root of the app, which also has a React.router which applies a Suspense promise on every render.
//      e.g.: <UseInComponent use={useDialogs} ctrl={appCtx} toJsx={ctrl => ctrl.dialogsJsx}/>
//
export function UseAsComponent({use, param, ctrl, toJsx}) {
    Object.assign(ctrl, use(param));
    return toJsx(ctrl);
}


export const transitions = {
    wipeDown: {enterFrom: "[clip-path:inset(0_0_100%_0)]", enterTo: "transition-all duration-100 ease-out [clip-path:inset(0_0_0_0)]"},
    wipeUp500: {enterFrom: "[clip-path:inset(100%_0_0_0)]", enterTo: "transition-all duration-500 ease-out [clip-path:inset(0_0_0_0)]"},
    wipeDownHide: {enterFrom: "[clip-path:inset(0_0_100%_0)]", enterTo: "transition-all duration-200 ease-out [clip-path:inset(0_0_0_0)]", leaveTo: "scale-90 opacity-0"},
    slideLeft: {enterFrom: "transform translate-x-full", enterTo: "translate-x-0"},
    fade100: {enterFrom: "opacity-0", enterTo: "transition-all duration-100 ease-out opacity-100"},
    dftBase: "transition-all duration-200 ease-out",
    dftFrom: "scale-90 opacity-0 pointer-events-none",     // Faded-out state — disable pointer events so leaving overlays don't block clicks during fade.
    dftTo: "scale-100 opacity-100",
};

const arrowBase = "rounded-lg py-2 flex flex-col px-3 z-10 items-center gap-1 text-xs shadow-sm text-title after:absolute after:w-0 after:h-0 after:bottom-full ";
export const arrowPlacementClasses = {
    left: arrowBase + "after:left-0 after:border-l-8 after:border-l-transparent after:border-r-8 after:border-r-transparent after:border-b-8 ml-[-1rem]",
    right: arrowBase + "after:right-0 after:border-r-8 after:border-r-transparent after:border-l-8 after:border-l-transparent after:border-b-8 mr-[-1rem]",
    middle: arrowBase + "after:left-1/2 after:border-l-8 after:border-l-transparent after:border-r-8 after:border-r-transparent after:border-b-8 ml-[-1rem]"
};

/**
 * Manage element transition effect stages.
 * @param transition - boolean|{enterFrom, enterTo, leaveFrom?, leaveTo?}: classes to apply depending on stage. If true or any class string missing, use default.
 * @param isShown - boolean?: if element should be shown or hidden.
 * @param onAfter - function(event, stage)?: callback to issue on onTransitionEnd events.
 * @return {{classNameTrans: string, refTrans: function, propsTrans}}
 */
export function useEffectTransition({transition, isShown, onAfter}) {
    const vals = useRef<{stage: number, elm?: Element}>({stage: 0}).current;
    const rerender = useRerender();

    useEffect(() => {
        if (!transition || (!vals.stage && !isShown)) return;
        vals.stage++;
        rerender();
    }, [!isShown]);

    if (isShown && vals.stage !== 2) vals.stage = 1;
    else if (!isShown && vals.stage && vals.stage !== 4) vals.stage = 3;

    const leaveTo = transition?.leaveTo ?? transition?.enterFrom;

    const classNameTrans = !transition? isShown && " " : [
        // 0: !isShown, dormant.
        () => "",
        // 1: isShown, pre-transition
        () => transition?.enterFrom ?? transitions.dftFrom,
        // 2: isShown, start-transition
        () => `${!transition.enterTo?.includes("transition-") && transitions.dftBase} ${transition.enterTo ?? transitions.dftTo}`,
        // 3: !isShown, pre-transition
        () => transition?.leaveFrom ?? transition.enterTo ?? transitions.dftTo,
        // 4: !isShown, start-transition
        () => `${!leaveTo?.includes("transition-") && transitions.dftBase} ${leaveTo ?? transitions.dftFrom}`
    ][vals.stage]();

    function onTransitionEnd(e) {
        if (e.target !== vals.elm) return;
        onAfter?.(e, vals.stage);
        if (vals.stage !== 4) return;
        vals.stage = 0;
        rerender();
    }

    return {
        classNameTrans,
        propsTrans: transition && {onTransitionEnd},
        refTrans: e => vals.elm = e
    };
}

export function Transition({show, transition, children, onAfter, className}) {
    const {classNameTrans, propsTrans, refTrans} = useEffectTransition({transition, isShown: show, onAfter});

    return !classNameTrans? null :
        <div ref={refTrans} className={`${classNameTrans} ${className}`} {...{...propsTrans, children}}/>;
}


/**
 * Manages the lifecycle of animated UI panels with open/close transitions and optional callbacks.
 * Allows panels to be closed with animations and optionally removed from the DOM after the transition completes.
 *
 * @param dispPanel - A render function that receives panel props and returns a JSX element.
 *                    Called with `{ panelId, ...props, onClose, onHideDone }`.
 *
 * @returns An object with:
 *   - `setPanel({ Elm, onClose?, ...otherProps })` – Opens or updates a panel:
 *       - `Elm` is the React component to render inside the panel.
 *       - `onClose?(...args)` is an optional callback triggered before closing.
 *           - If it returns `false`, the panel will **not** close.
 *           - Can be used by components like `MessageBox` to differentiate actions (e.g. OK vs Cancel).
 *            e.g.: MessageBox issues onClose(true) on its onOk button, to tell between cancel and OK.
 *       - Panels can also register their own handler via `onClose.onClose = fn(...)` to intercept closing.
 *
 *   - `closePanel(panelId, ...args)` – Attempts to close the panel. Respects the `onClose` contract.
 *
 *   - `panelsJsx` – A JSX fragment that must be rendered in the caller’s render tree. Contains the active panels.
 *
 *   - `isPanelOn(panelId)` – Returns `true` if a panel is open and visible.
 *
 * @example
 * const useSidePanel = () =>
 *   useDynamicPanels(({panelId, ...props}) => <SidePanel key={panelId} {...props} />);
 */
export function useDynamicPanels(dispPanel) {
    const {state: st, rerender} = useStateEx<any>({panels: [], panelsJsx: []});

    const closePanel = (panelId, ...args) =>
        st.panels[panelId]?.props.onClose?.(...args) !== false &&   // check caller's optional onClose CB
        setPanel({panelId, hide: true});

    function setPanel({panelId, ...props}) {
        panelId = panelId ?? st.panels.length;
        props = {...st.panels[panelId]?.props, hide: props.hide, ...props};
        const onHideDone = _ => setPanel({panelId, Elm: null});
        const onClose: ((...args: any[]) => any) & {onClose?: (...args: any[]) => any} =
            (...args) => onClose.onClose?.(...args) !== false && closePanel(panelId, ...args);      // check panel's optional internal onClose CB
        st.panels[panelId] = props.Elm? {props, jsx: dispPanel({panelId, ...props, onClose, onHideDone})} : null;
        st.panelsJsx = st.panels.filter(Boolean).map(p => p.jsx);
        rerender();
        return panelId;
    }

    const closeAllPanels = () => st.panels.forEach((p, i) => p && !p.props.hide && closePanel(i));

    return {
        setPanel, closePanel, closeAllPanels, panelsJsx: st.panelsJsx,
        isPanelOn: panelId => st.panels[panelId] && !st.panels[panelId].props.hide,
    };
}

export function OverlayUnderpanel({classNameOverlay, classNameTrans, onClose, children}) {
    const rOverlay = useRef(null);
    return !classNameTrans? null :
        !classNameOverlay?
            children() :
            <div
                className={`fixed inset-0 bg-opacity-30 z-[100] ${classNameOverlay} ${classNameTrans}`}
                ref={rOverlay} onClick={e => e.target === rOverlay.current && onClose(e)}>
                {children()}
            </div>;
}

export const useScreenSize = () => {
    const getScreenSize = () => ({width: window.innerWidth, height: window.innerHeight});
    const [screenSize, setScreenSize] = useState(getScreenSize());
    useEventListener({sEv: "resize", cb: () => setScreenSize(getScreenSize())});
    return screenSize;
};

export const useBoxSize = (initialState) => {
    const [boxSize, setBoxSize] = useState(initialState);
    const boxRef = useRef<HTMLDivElement>(null);
    const handleResize = () => boxRef.current && setBoxSize({
        height: boxRef.current.offsetHeight || initialState.height,
        width: boxRef.current.offsetWidth || initialState.width
    });

    useLayoutEffect(() => {
        handleResize();
    }, []);

    useObserver(ResizeObserver, boxRef, "width", handleResize, {debounceMs: 100});   // allow animated box to redraw after initial layout or scrollbar appearance

    return {boxRef, boxSize};
};


// Allows to align column widths of elements in table like list.
// Width of all elements in a column will be fixed to the one with the max scrollWidth.
// Set className of each element playing as a column to col-<number> starting 1.
//
export function fixColumnWidths(parent, nCols) {
    const cols = makeArr(nCols).map(iCol => [...(getElmOfRef(parent) ?? document).getElementsByClassName(`col-${iCol+1}`)]);
    const widths = cols.map(elems => elems.reduce((a, b) => Math.max(a, b.scrollWidth), 0));
    cols.forEach((elems, iCol) => elems.forEach(el => el.style.minWidth = `${widths[iCol]}px`));
}


// rows: [{a: "blah", b: "blah"}, ...]
// hdr: {a: "Title A", b: "Title B"}     - in order of appearance
export function genTable({rows, hdr, funcs, className, classNameHdrRow, classNameHdrCells, classNameRows, classNameCells, classNames, dataTest}) {
    hdr = hdr ?? Object.keys(rows[0]).reduce((a, b) => ((a[b] = b), a), {});     // if no hdr, take hdr from keys of first object.
    return <table data-test={dataTest} className={className}>
        <tbody>
            {hdr &&
            <tr className={classNameHdrRow}>
                {Object.keys(hdr).map(key => <td key={key} className={classNameHdrCells ?? classNameCells}>{hdr[key]}</td>)}
            </tr>}
            {rows.map((row, i) =>
                <tr key={i} className={classNameRows}>
                    {Object.keys(hdr || row).map(key => {
                        const v = row[key], val = typeof v === "object"? v?.val : v;
                        const disp = (v?.func ?? funcs?.[key] ?? funcs?.[typeof val])?.(val, row) ?? val;
                        return <td
                            data-test={`${dataTest}-${key}`} key={key}
                            className={v?.className ?? classNames?.[key] ?? classNameCells}
                            onMouseEnter={tooltipIfNeeded}>{disp}</td>;
                    })}
                </tr>)}
        </tbody>
    </table>;
}


export function usePublicFileLoader({fileName, onData = undefined, onErr = undefined, disabled = false}) {
    const [data, setData] = useState(null);
    const fn = `${import.meta.env.BASE_URL || ""}${fileName}`;

    useEffect(() => {
        if (disabled) return;
        fetch(fn)
            .then(res => res.text())
            .then(v => setData(onData? onData(v) : v))
            .catch(err => {
                console.error(`Could not load text file "${fn}": `, err);
                setData(onErr? onErr(err.toString()) : err);
            });
    }, [fileName]);

    return data;
}

export function ellipsisText(text: string, maxLen: number) {
    if (!text || text.length <= maxLen) return text;
    return text.slice(0, maxLen - 3) + "...";
}
